import React, { forwardRef, useMemo, useState } from 'react'

import { PageIndicator } from 'partials/EventPostCard/subcomponents/PageIndicator'
import { MediaPlayer } from 'partials/PostPlayer/widgets/MediaPlayer'

import { Box } from 'widgets/Box'
import { Icon } from 'widgets/Icon'
import { Pressable } from 'widgets/Pressable'

import { getFileTypeFromMimeType } from 'utils/media'

import { StoredPostStep } from 'types/post'

type PostMediaPreviewProps = {
  files: StoredPostStep[]
  isOwner: boolean
  tapped: () => void
  autoplay: boolean
  muted: boolean
  setIsMuted: (muted: boolean) => void
}

export const PostMediaPreview = forwardRef(
  (
    { files, isOwner, tapped, autoplay = false, muted = false, setIsMuted }: PostMediaPreviewProps,
    ref,
  ) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const { file } = files[currentImageIndex]
    const mimeType = file?.mimeType ?? 'image'
    const isVideo = useMemo(() => getFileTypeFromMimeType(mimeType) === 'video', [mimeType])

    return (
      <Box flex={1} align='center' justifyContent='center' pointerEvents='box-none'>
        <MediaPlayer ref={ref} file={file} autoplay={autoplay} muted={muted} isPreview />

        <Box
          pointerEvents='box-none'
          position='absolute'
          align='flex-end'
          justifyContent='flex-end'
          width='100%'
          height='100%'
        >
          {files &&
            files.length !== 0 && ( // Tapable area
              <>
                {isVideo && isOwner && (
                  <Pressable onPress={() => setIsMuted(!muted)}>
                    <Box align='center' justifyContent='center' width={44} height={44}>
                      <Icon name={muted ? 'volume-x' : 'volume-2'} />
                    </Box>
                  </Pressable>
                )}
              </>
            )}
        </Box>

        <Box // left, right buttons
          width='100%'
          position='absolute'
          pointerEvents='box-none'
          flexDirection='row'
          justifyContent='space-between'
          align='center'
        >
          <Box padding={{ vertical: 'xl' }}>
            <Pressable
              disabled={currentImageIndex === 0}
              onPress={() => {
                if (currentImageIndex) {
                  setCurrentImageIndex((index) => index - 1)
                }
              }}
            >
              <Icon
                name='chevron-left'
                color={currentImageIndex === 0 ? 'transparent' : 'white'}
                size={32}
              />
            </Pressable>
          </Box>
          <Box padding={{ vertical: 'xl' }}>
            <Pressable
              disabled={currentImageIndex + 1 === files?.length}
              onPress={() => {
                if (currentImageIndex + 1 < (files ? files.length : 0)) {
                  setCurrentImageIndex((index) => index + 1)
                }
              }}
            >
              <Icon
                name='chevron-right'
                color={currentImageIndex + 1 === files?.length ? 'transparent' : 'white'}
                size={32}
              />
            </Pressable>
          </Box>
        </Box>

        {files.length > 1 && (
          <Box
            width='100%'
            height='100%'
            padding='sm'
            pointerEvents='none'
            position='absolute'
            align='center'
            justifyContent='flex-end'
          >
            <PageIndicator
              key={`${files[currentImageIndex].file?.url}`}
              size='sm'
              pagesCount={files.length}
              selectedIndex={currentImageIndex}
              selectedColor='v2primary'
              unselectedColor='greyLight'
            />
          </Box>
        )}
      </Box>
    )
  },
)
